// Sales Documents — only real backend fields (salesDocSchema + /api/sales-docs).
// No category / file_name / tags / version / status (none are persisted).

// 'partially_paid' is additive (client-approved audit fix — SalesDoc payments
// now route through the real Accounts allocation/posting engine, see
// src/api-accounts/salesdoc-payment-bridge.js on the backend). Every existing
// status value is unchanged.
export type SalesDocStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'finalized' | 'paid' | 'overdue' | 'partially_paid';
export type SalesDocType = 'proposal' | 'quotation' | 'proforma' | 'scope_of_services' | 'invoice' | 'credit_note' | 'debit_note' | 'other';

export interface SalesDoc {
  id: string;
  title: string;
  description: string;
  url: string; // Used as filename internally
  type: SalesDocType;
  creator_name?: string;
  /**
   * Owner reference as returned by `GET /api/sales-docs`: the backend populates
   * `uploaded_by` to `{ _id, display_name }` (or leaves the raw id) — a frozen
   * contract field (see api-v2/contract-freeze.test.js). Used for client-side
   * ownership hints only; the server remains the authorization source of truth.
   */
  uploaded_by?: string | { id?: string; _id?: string; display_name?: string } | null;
  created_at: string;
  status?: SalesDocStatus;
  amount?: number;
  /**
   * Remaining balance owed (client-approved audit fix). Mirrors the linked
   * AccountsSalesInvoice's `amountDue` once a payment has been recorded
   * through the Accounts bridge; `null`/`undefined` only for documents the
   * backend hasn't computed it for yet (non-invoice doc types, or a
   * not-yet-migrated legacy row the compute-on-read fallback missed).
   */
  amountDue?: number | null;
  reference_number?: string;
  valid_until?: string;
  related_to_type?: 'lead' | 'deal' | null;
  related_to_id?: string | null;
  is_generated?: boolean;
  template_data?: any;
  /** null = unfiled, sitting outside any folder. */
  folderId?: string | null;
}

export interface SalesDocFolder {
  id: string;
  name: string;
  createdAt: string;
  docCount: number;
}

export interface CreateSalesDocPayload {
  title: string;
  description?: string;
  url?: string;
  type?: SalesDocType;
  status?: SalesDocStatus;
  amount?: number;
  relatedToType?: 'lead' | 'deal' | null;
  relatedToId?: string | null;
  reference_number?: string;
  valid_until?: string | null;
  is_generated?: boolean;
  template_data?: any;
  folderId?: string | null;
}

export type UpdateSalesDocPayload = Partial<CreateSalesDocPayload>;

export function getUploaderId(u: SalesDoc['uploaded_by']): string {
  if (!u) return '';
  if (typeof u === 'string') return u;
  return u.id ?? u._id ?? '';
}

export type PaymentMethod = 'Cash' | 'Bank Transfer' | 'UPI' | 'Credit Card' | 'Cheque';

export interface Payment {
  id: string;
  invoice_id: string;
  invoice_title?: string;
  invoice_amount?: number;
  amount_paid: number;
  payment_method: PaymentMethod;
  payment_date: string;
  transaction_reference?: string;
  notes?: string;
  logged_by?: string;
  logged_by_name?: string;
  created_at?: string;
}

export interface CreatePaymentPayload {
  invoice_id: string;
  amount_paid: number;
  payment_method: PaymentMethod;
  payment_date: string;
  transaction_reference?: string;
  notes?: string;
  retainExcessAsAdvance?: boolean;
}
